import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  adminCourseStepSectionGetAction,
  adminStepSectionQuestionCreateAction,
  adminStepSectionQuestionGetAction,
  adminStepSectionQuestionUpdateAction
} from "../Action/action";

function newOptionKey() {
  return `opt-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Maps API question_type to form value; supports legacy rows stored as single with no options. */
function formQuestionTypeFromApi(apiType, optionRowsCount) {
  const t = String(apiType || '').toLowerCase();
  if (t === 'multiple') return 'multiple';
  if (t === 'text') return 'text';
  if (t === 'single' && optionRowsCount === 0) return 'text';
  return 'single';
}

function mapApiOptionsToRows(apiOpts) {
  if (!Array.isArray(apiOpts)) return [];
  return apiOpts.map((o, i) => ({
    key: `db-${o.id}`,
    id: o.id,
    optionText: o.title || "",
    displayOrder: o.display_order != null && o.display_order !== "" ? String(o.display_order) : String(i),
    isActive: Number(o.is_active) === 1 ? "1" : "0"
  }));
}

export default function StepSectionQuestionForm({ mode }) {
  const { sectionId, questionId } = useParams();
  const navigate = useNavigate();

  const listPath = `/step-sections/${sectionId}/questions`;

  const [loadingRow, setLoadingRow] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [sectionTitle, setSectionTitle] = useState("");

  const [title, setTitle] = useState("");
  const [questionType, setQuestionType] = useState("single");
  const [placeholder, setPlaceholder] = useState("");
  const [displayOrder, setDisplayOrder] = useState("0");
  const [isActive, setIsActive] = useState("1");
  const [options, setOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sRes = await adminCourseStepSectionGetAction(sectionId);
        if (cancelled) return;
        if (sRes?.success) setSectionTitle(sRes.data?.title || "");
      } catch (e) {}
    })();
    return () => {
      cancelled = true;
    };
  }, [sectionId]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (mode !== "edit") {
        setOptions([{ key: newOptionKey(), id: null, optionText: "", displayOrder: "1", isActive: "1" }]);
        return;
      }
      setLoadingRow(true);
      setError("");
      try {
        const res = await adminStepSectionQuestionGetAction(sectionId, questionId);
        if (cancelled) return;
        if (!res?.success) {
          setError(res?.message || "Failed to load question.");
          return;
        }
        const r = res.data || {};
        setTitle(r.title || "");
        setPlaceholder(r.placeholder != null ? String(r.placeholder) : "");
        const mapped = mapApiOptionsToRows(r.options);
        const qt = formQuestionTypeFromApi(r.question_type, mapped.length);
        setQuestionType(qt);
        const ord = r.display_order;
        setDisplayOrder(
          ord != null && ord !== "" && Number.isFinite(Number(ord)) ? String(Math.max(0, parseInt(String(ord), 10))) : "0"
        );
        setIsActive(Number(r.is_active) === 1 ? "1" : "0");
        if (qt === "text") {
          setOptions([]);
        } else {
          setOptions(mapped.length ? mapped : [{ key: newOptionKey(), id: null, optionText: "", displayOrder: "0", isActive: "1" }]);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || "Failed to load.");
      } finally {
        if (!cancelled) setLoadingRow(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [sectionId, questionId, mode]);

  const addOptionRow = () => {
    setOptions((prev) => {
      const allFilled = prev.every((o) => String(o.optionText || "").trim().length > 0);
      if (!allFilled) {
        toast.error("Fill in option text for each row before adding another.", { toastId: "ssq-add-option" });
        return prev;
      }
      return [
        ...prev,
        {
          key: newOptionKey(),
          id: null,
          optionText: "",
          displayOrder: String(prev.length + 1),
          isActive: "1"
        }
      ];
    });
  };

  const removeOptionRow = (key) => {
    setOptions((prev) => (prev.length <= 1 ? prev : prev.filter((o) => o.key !== key)));
  };

  const updateOption = (key, patch) => {
    setOptions((prev) => prev.map((o) => (o.key === key ? { ...o, ...patch } : o)));
  };

  const validate = () => {
    const errs = {};
    if (!String(title || "").trim()) errs.title = "Title is required.";
    const ordRaw = String(displayOrder ?? "").replace(/\D/g, "");
    const ord = ordRaw === "" ? 0 : parseInt(ordRaw, 10);
    if (!Number.isFinite(ord) || ord < 0) errs.displayOrder = "Display order must be zero or a positive number.";
    return errs;
  };

  const buildOptionsPayload = () => {
    return options
      .map((o, i) => {
        const text = String(o.optionText || "").trim();
        const dRaw = String(o.displayOrder ?? "").replace(/\D/g, "");
        const d = dRaw === "" ? i : parseInt(dRaw, 10);
        return {
          ...(o.id ? { id: o.id } : {}),
          title: text,
          display_order: Number.isFinite(d) && d >= 0 ? d : i,
          is_active: o.isActive === "1" ? 1 : 0
        };
      })
      .filter((o) => o.title.length > 0);
  };

  const onSave = async () => {
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please fix the required fields.", { toastId: "ssq-form-save" });
      return;
    }

    setSaving(true);
    setError("");
    try {
      const ordStr = String(displayOrder ?? "").replace(/\D/g, "");
      const display_order = ordStr === "" ? 0 : Math.max(0, parseInt(ordStr, 10));
      const isTextAnswer = questionType === "text";
      const body = {
        title: String(title).trim(),
        question_type: questionType,
        display_order,
        is_active: isActive === "1" ? 1 : 0,
        options: isTextAnswer ? [] : buildOptionsPayload(),
        ...(isTextAnswer ? { placeholder: String(placeholder ?? "").trim() } : {})
      };

      const res =
        mode === "edit"
          ? await adminStepSectionQuestionUpdateAction(sectionId, questionId, body)
          : await adminStepSectionQuestionCreateAction(sectionId, body);
      if (!res?.success) throw new Error(res?.message || "Save failed");

      toast.success(mode === "edit" ? "Question updated" : "Question created", { toastId: "ssq-form-save" });
      navigate(listPath);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Save failed";
      setError(msg);
      toast.error(msg, { toastId: "ssq-form-save" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content">
      {mode === "edit" && loadingRow ? (
        <div className="loading-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="loading-spinner" aria-hidden="true" />
        </div>
      ) : null}

      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Could not load/save question.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div className="breadcrumb" style={{ marginBottom: 0 }}>
          <Link to="/step-sections">Step sections</Link>
          <span>›</span>
          <span>{sectionTitle || "Section"}</span>
          <span>›</span>
          <Link to={listPath}>Questions</Link>
          <span>›</span>
          <span>{mode === "edit" ? "Edit" : "Add"}</span>
        </div>
        <Link to={listPath} className="btn btn-outline btn-sm">
          ← Back
        </Link>
      </div>

      <div className="card mb-6">
        <h2 className="section-title">Question</h2>

        <div className="form-group">
          <label>
            Title <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setFieldErrors((p) => ({ ...p, title: "" }));
            }}
            aria-invalid={!!fieldErrors.title}
            style={fieldErrors.title ? { borderColor: "var(--danger)" } : undefined}
          />
          {fieldErrors.title ? (
            <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
              {fieldErrors.title}
            </div>
          ) : null}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Answer type</label>
            <select
              className="form-control"
              value={questionType}
              onChange={(e) => {
                const v = e.target.value;
                setQuestionType(v);
                if (v === "text") {
                  setOptions([]);
                } else {
                  setPlaceholder("");
                  setOptions((prev) =>
                    prev.length === 0
                      ? [{ key: newOptionKey(), id: null, optionText: "", displayOrder: "0", isActive: "1" }]
                      : prev
                  );
                }
              }}
            >
              <option value="single">Single answer</option>
              <option value="multiple">Multiple answers</option>
              <option value="text">Text answer</option>
            </select>
            {questionType === "text" ? (
              <p className="small-label" style={{ marginTop: 8 }}>
                Stored as type text with no predefined options; the learner types their answer in the app.
              </p>
            ) : null}
          </div>
          <div className="form-group">
            <label>Display order</label>
            <input
              type="text"
              inputMode="numeric"
              className="form-control"
              placeholder="0"
              value={displayOrder}
              onInput={(e) => {
                const v = e.target.value.replace(/\D/g, "");
                setDisplayOrder(v);
                setFieldErrors((p) => ({ ...p, displayOrder: "" }));
              }}
              aria-invalid={!!fieldErrors.displayOrder}
              style={fieldErrors.displayOrder ? { borderColor: "var(--danger)" } : undefined}
            />
            {fieldErrors.displayOrder ? (
              <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                {fieldErrors.displayOrder}
              </div>
            ) : null}
          </div>
          <div className="form-group">
            <label>Status</label>
            <select className="form-control" value={isActive} onChange={(e) => setIsActive(e.target.value)}>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>
        </div>

        {questionType === "text" ? (
          <div className="form-group">
            <label>Placeholder</label>
            <input
              type="text"
              className="form-control"
              placeholder="Hint shown in the empty answer field"
              value={placeholder}
              onChange={(e) => setPlaceholder(e.target.value)}
            />
            <p className="small-label" style={{ marginTop: 8 }}>
              Optional. Shown inside the learner's text input in the app when the field is empty.
            </p>
          </div>
        ) : null}
      </div>

      {questionType !== "text" ? (
        <div className="card mb-6">
          <div className="flex items-center justify-between" style={{ marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
            <h2 className="section-title" style={{ margin: 0 }}>
              Answer options
            </h2>
            <button type="button" className="btn btn-outline btn-sm" onClick={addOptionRow}>
              + Add option
            </button>
          </div>
          <p className="small-label" style={{ marginBottom: 16 }}>
            Rows with empty option text are skipped on save. Options you remove here are deleted when you update the question.
          </p>

          <div className="table-wrapper" style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Option text</th>
                  <th style={{ width: 150 }}>Display order</th>
                  <th style={{ width: 140 }}>Status</th>
                  <th style={{ width: 90 }} />
                </tr>
              </thead>
              <tbody>
                {options.map((o) => (
                  <tr key={o.key}>
                    <td>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Option text"
                        value={o.optionText}
                        onChange={(e) => updateOption(o.key, { optionText: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-control"
                        value={o.displayOrder}
                        onInput={(e) => {
                          const v = e.target.value.replace(/\D/g, "");
                          updateOption(o.key, { displayOrder: v });
                        }}
                      />
                    </td>
                    <td>
                      <select
                        className="form-control"
                        value={o.isActive}
                        onChange={(e) => updateOption(o.key, { isActive: e.target.value })}
                      >
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                      </select>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm text-danger"
                        onClick={() => removeOptionRow(o.key)}
                        disabled={options.length <= 1}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="flex gap-3">
        <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving || loadingRow}>
          {saving ? "Saving…" : "Save"}
        </button>
        <Link to={listPath} className="btn btn-outline">
          Cancel
        </Link>
      </div>
    </div>
  );
}
